import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { processedCostByService } from './Services/reportServices';
import typeOfContract from '../../../constants/typeOfContract';

const data = [
  { name: 'Rent', cost: 144, invoice: 124 },
  { name: 'Utilities', cost: 155, invoice: 139 },
  { name: 'Groceries', cost: 113, invoice: 130 },
  { name: 'Transport', cost: 143, invoice: 150 },
  { name: 'Entertainment', cost: 146, invoice: 127 },
  { name: 'Healthcare', cost: 155, invoice: 140 },
  { name: 'Miscellaneous', cost: 132, invoice: 118 },
];



const ServiceCostGraph = ({ trackReport, trackReportLoading, trackReportError, projectCost, filteredCurrencies }) => {
  const reportingServices = useSelector((state) => state.reportingServices);
  const { forex } = useSelector((state) => state.forex);
  const settings = JSON.parse(localStorage.getItem("settings"));


  var currencies = "USD";
  if (filteredCurrencies && filteredCurrencies.length === 1) {
    currencies = filteredCurrencies[0];
  } else {
    currencies = settings.currency;
  }

  // const processedData = useMemo(() => {
  //   return processedCostByService(trackReport, projectCost, reportingServices, currencies, forex, typeOfContract);
  // }, [trackReport, projectCost, reportingServices, settings, forex]);
  const processedData = useMemo(() => {
    const data = processedCostByService(trackReport, projectCost, reportingServices, currencies, forex, typeOfContract);
    return data.sort((a, b) => a.name.localeCompare(b.name));
  }, [trackReport, projectCost, reportingServices, settings, forex]);

  const calculateXTicks = () => {
    if (!processedData.length) return [];
    const maxDataValue = Math.max(...processedData.map(item => item.totalCost));
    if (maxDataValue === 0) return [0];
    const tickInterval = Math.ceil(maxDataValue / 10);
    const numOfTicks = Math.ceil(maxDataValue / tickInterval);
    const ticks = [];

    for (let i = 0; i <= numOfTicks + 4; i++) { // Adjust the number of ticks
      ticks.push(i * tickInterval);
    }

    return ticks;
  }

  const xAxisTicks = calculateXTicks();

  if (trackReportLoading) {
    const skeletonRows = 8;
    const rowHeight = 50;
    const baseHeight = 100;
    const skeletonHeight = skeletonRows * rowHeight + baseHeight;
    
    // Fixed widths for skeleton bars to avoid random values
    const barWidths = [45, 60, 35, 55, 40, 50, 38, 48];
    
    return (
      <div className='graph-project-per-hour' style={{ width: '100%', height: Math.max(542, skeletonHeight) }}>
        <div style={{ 
          width: '100%', 
          height: '100%', 
          padding: '20px 42px 5px 30px',
          position: 'relative'
        }}>
          {/* Y-axis skeleton labels */}
          <div style={{ 
            position: 'absolute', 
            left: '0', 
            top: '20px', 
            bottom: '5px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingRight: '10px'
          }}>
            {Array.from({ length: skeletonRows }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '120px',
                  height: '13px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>

          {/* Chart area with bars */}
          <div style={{ 
            marginLeft: '150px',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingTop: '10px',
            paddingBottom: '30px'
          }}>
            {Array.from({ length: skeletonRows }).map((_, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '50px',
                  position: 'relative'
                }}
              >
                {/* Single bar skeleton */}
                <div
                  style={{
                    width: `${barWidths[index] || 45}%`,
                    height: '13px',
                    backgroundColor: '#86ABE1',
                    opacity: 0.3,
                    borderRadius: '2px',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                />
              </div>
            ))}
          </div>

          {/* X-axis skeleton ticks */}
          <div style={{ 
            position: 'absolute', 
            bottom: '5px', 
            left: '150px', 
            right: '42px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end'
          }}>
            {Array.from({ length: 6 }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '40px',
                  height: '12px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }

  if (trackReportError) return <div>Error loading data.</div>;

  const calculateChartHeight = (dataLength) => {
    const rowHeight = 50;
    const baseHeight = 100;
    return dataLength * rowHeight + baseHeight;
  };

  const calculateLeftMargin = (data) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    context.font = '12px Arial';

    const longestWordLength = data.reduce((maxLength, item) => {
      const firstWord = item.name.split(' ')[0];
      const wordWidth = context.measureText(firstWord).width;
      return Math.max(maxLength, wordWidth);
    }, 0);

    return Math.max(20, longestWordLength - 140);
  };

  const leftMargin = calculateLeftMargin(processedData);

  return (
    <div className='graph-project-per-hour' style={{ width: '100%', height: Math.max(542, calculateChartHeight(processedData.length)), }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          layout="vertical"
          data={processedData}
          barSize={13}
          margin={{
            top: 20,
            right: 42,
            left: leftMargin,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" horizontal={false} vertical={true} />
          <XAxis
            type="number"
            domain={[0, 'dataMax + 10']}
            tickFormatter={(tick) => `${new Intl.NumberFormat('en-US', { style: 'currency', currency: currencies, }).format(0).replace(/[\d.,]/g, '')}${tick}`}
            ticks={xAxisTicks}
          />
          <YAxis
            type="category"
            dataKey="name"
            // width={150}
            tickMargin={10}
          />
          <Tooltip formatter={(value) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: currencies, }).format(value)}`} />
          <Bar dataKey="totalCost" name="Total Cost" fill="#86ABE1">
            <LabelList dataKey="totalCost" position="right" formatter={(value) => `${new Intl.NumberFormat("en-US", { style: "currency", currency: currencies, }).format(value)}`} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default ServiceCostGraph;
